import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import { FontFamily } from '@tiptap/extension-font-family';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Placeholder from '@tiptap/extension-placeholder';
import { FontSize } from './fontSize';
import { PageNumberField, type FieldKind } from './pageNumberField';
import { store } from '@/state/store';
import { icon, type IconName } from '@/icons/icons';

// ═══════════════════════════════════════════════════════════════
// Záhlaví a zápatí (sekce 2.2, Fáze 2).
//
// Model: dva samostatné TipTap editory (nad a pod hlavní stránkou) s plným
// inline formátováním. Obsah se drží jako HTML v store (doc.headerHtml /
// doc.footerHtml) a odtud jde do DOCX exportu i autosave.
//
// UX: kvůli tomu, že hlavní ribbon je navázaný na tělo dokumentu, mají
// panely vlastní kontextový mini-toolbar (tučné/kurzíva/podtržení,
// zarovnání, vložit číslo stránky), který se objeví při zaměření panelu.
// ═══════════════════════════════════════════════════════════════

function panelExtensions(placeholder: string) {
  return [
    StarterKit.configure({
      heading: false, // v záhlaví/zápatí nedávají nadpisy smysl
      // seznamy ponecháme, bold/italic/underline/strike jsou v kitu
    }),
    TextStyle,
    FontSize,
    Color,
    Highlight.configure({ multicolor: true }),
    TextAlign.configure({ types: ['paragraph'] }),
    FontFamily,
    Subscript,
    Superscript,
    PageNumberField,
    Placeholder.configure({ placeholder }),
  ];
}

export class HeaderFooter {
  header: Editor;
  footer: Editor;
  private headerHost: HTMLElement;
  private footerHost: HTMLElement;

  constructor(container: HTMLElement) {
    // Struktura: [záhlaví panel] [ (hlavní stránka je jinde) ] — panely se
    // vkládají do wrapperu kolem .doc-page (viz integrace v main.ts).
    this.headerHost = document.createElement('div');
    this.headerHost.className = 'doc-hf doc-header';
    this.footerHost = document.createElement('div');
    this.footerHost.className = 'doc-hf doc-footer';

    const headerLabel = labelEl('Záhlaví');
    const footerLabel = labelEl('Zápatí');
    const headerMount = document.createElement('div');
    const footerMount = document.createElement('div');
    headerMount.className = 'doc-hf-mount';
    footerMount.className = 'doc-hf-mount';

    this.headerHost.append(headerLabel, headerMount);
    this.footerHost.append(footerLabel, footerMount);

    const s = store.get();
    this.header = new Editor({
      element: headerMount,
      extensions: panelExtensions('Záhlaví…'),
      content: s.doc.headerHtml || '<p></p>',
      editorProps: { attributes: { class: 'doc-hf-content' } },
      onUpdate: ({ editor }) => {
        store.patchDoc({ headerHtml: editor.getHTML(), dirty: true });
      },
    });
    this.footer = new Editor({
      element: footerMount,
      extensions: panelExtensions('Zápatí…'),
      content: s.doc.footerHtml || '<p></p>',
      editorProps: { attributes: { class: 'doc-hf-content' } },
      onUpdate: ({ editor }) => {
        store.patchDoc({ footerHtml: editor.getHTML(), dirty: true });
      },
    });

    this.headerHost.prepend(this.buildToolbar(this.header));
    this.footerHost.append(this.buildToolbar(this.footer));

    // Panely zpřístupníme volajícímu k umístění kolem stránky.
    container.dataset.hfReady = 'true';
  }

  /** Vloží panely do DOM okolo dané stránky (svislý sloupec: záhlaví/stránka/zápatí). */
  mountAround(page: HTMLElement): void {
    // .page-wrap je vodorovný flex (centrování stránky). Kdybychom panely
    // vložili jako sourozence stránky, naskládají se VEDLE ní. Proto stránku
    // i panely obalíme do svislého sloupce, který se v page-wrap centruje jako
    // jeden blok.
    const parent = page.parentElement!;
    const column = document.createElement('div');
    column.className = 'doc-hf-column';
    parent.insertBefore(column, page);
    column.append(this.headerHost, page, this.footerHost);
  }

  /** Přepíše obsah panelů (např. po otevření dokumentu). */
  setContent(headerHtml: string, footerHtml: string): void {
    this.header.commands.setContent(headerHtml || '<p></p>');
    this.footer.commands.setContent(footerHtml || '<p></p>');
  }

  private buildToolbar(ed: Editor): HTMLElement {
    const bar = document.createElement('div');
    bar.className = 'doc-hf-toolbar';

    const btn = (ic: IconName, title: string, fn: () => void) => {
      const b = document.createElement('button');
      b.className = 'doc-hf-tbtn';
      b.title = title;
      b.innerHTML = icon(ic, 15);
      b.addEventListener('mousedown', (e) => e.preventDefault());
      b.addEventListener('click', fn);
      return b;
    };

    bar.append(
      btn('bold', 'Tučné (Ctrl+B)', () => ed.chain().focus().toggleBold().run()),
      btn('italic', 'Kurzíva (Ctrl+I)', () => ed.chain().focus().toggleItalic().run()),
      btn('underline', 'Podtržení (Ctrl+U)', () => ed.chain().focus().toggleUnderline().run()),
      sep(),
      btn('alignLeft', 'Vlevo', () => ed.chain().focus().setTextAlign('left').run()),
      btn('alignCenter', 'Na střed', () => ed.chain().focus().setTextAlign('center').run()),
      btn('alignRight', 'Vpravo', () => ed.chain().focus().setTextAlign('right').run()),
      sep(),
      fieldBtn(ed, 'page', '# Číslo stránky'),
      fieldBtn(ed, 'pages', '## Počet stran'),
    );
    return bar;
  }

  destroy(): void {
    this.header.destroy();
    this.footer.destroy();
    this.headerHost.remove();
    this.footerHost.remove();
  }
}

function fieldBtn(ed: Editor, kind: FieldKind, label: string): HTMLElement {
  const b = document.createElement('button');
  b.className = 'doc-hf-tbtn doc-hf-field';
  b.textContent = kind === 'pages' ? '##' : '#';
  b.title = label;
  b.addEventListener('mousedown', (e) => e.preventDefault());
  b.addEventListener('click', () => ed.chain().focus().insertPageField(kind).run());
  return b;
}

function labelEl(text: string): HTMLElement {
  const l = document.createElement('div');
  l.className = 'doc-hf-label';
  l.textContent = text;
  return l;
}

function sep(): HTMLElement {
  const s = document.createElement('span');
  s.className = 'doc-hf-sep';
  return s;
}
